import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HubFeature, QlikCmRole, WebUserAccount } from '@ats/api/cm';
import {
	AppUser,
	AppUserType,
	CmAuthService,
	CmBaseUserService,
	CmFeatureService,
	CmPublicTokenData,
	CmSettingsService,
} from '@ats/cm/core';
import { AtsLoggerService } from '@ats/logger';
import { AtsUtilityService } from '@ats/utility';
import { Apollo, gql } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { Observable, of, throwError } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { LocalizationHubUsers } from '../data';

const QUERY_CURRENT_USER = (features: string[]): DocumentNode => gql`
	query appUser {
		webUserAccount {
			id
			permissionList {
				id
				name
				key
			}
			login
			webUser {
				id
				name
				email
				login
				disabled
				internal
				admin
				isDuplicateEmail
				candRestrictType
				${features.find(feat => feat === HubFeature.ReportsAdvanced) ? 'qlik' : ''}
			}
		}
	}
`;

@Injectable({
	providedIn: 'root',
})
export class IpUserService extends CmBaseUserService {
	/** Gets the status of a user's ability to edit and add hub localizations */
	canLocalizeHub$: Observable<boolean>;

	constructor(
		_auth: CmAuthService,
		_settings: CmSettingsService,
		_features: CmFeatureService,
		_logger: AtsLoggerService,
		_http: HttpClient,
		private utilities: AtsUtilityService,
		private apollo: Apollo
	) {
		super(_auth, _settings, _features, _logger, _http);
		this.queryUser$ = this.getUserQuery$();
		this.canLocalizeHub$ = this.getCanLocalizeHubStatus$();
		this.monitorUserChanges$();
	}

	/**
	 * Starts the login process by passing the username and password down to get a token
	 * @method
	 * @param {Object} obj - An object
	 * @param {string} [obj.username] - The username of the user
	 * @param {string} obj.password - The password of the user
	 * @return {Observable<boolean>} - An observable that emits true if the user was successfully logged in
	 */
	public getUserByCredentials({ username, password }: { username: string; password: string }): Observable<boolean> {
		return this.auth
			.login$({
				clientAlias: this.settings.client,
				webUserLoginId: username,
				webUserPassword: password,
			})
			.pipe(take(1));
	}

	/**
	 * Starts the logout process
	 * @method
	 * @return {void} - Logs out the current user, then redirects to the login page
	 */
	public clearUser(): void {
		this.auth.logout$();
	}

	/**
	 * Impersonates a user or candidate
	 * @method
	 * @param {string} login - The username of the user or candidate
	 * @param {'user'|'candidate'} type - Specifies if we are impersonating a user or candidate
	 * @param {boolean} [localize=false] - Wether the user should be able to localize or not
	 * @return {Observable<AppUser | boolean>} - Returns the app user (if impersonating a user) on success, a boolean for candidates
	 */
	public impersonateUser(login: string): Observable<boolean> {
		return this.auth.updateTokenWithPost$(`${this.settings.api}/impersonate?webUserLoginId=${login}`).pipe(take(1));
	}

	/**
	 * Impersonates a user or candidate
	 * @method
	 * @param {string} login - The username of the user or candidate
	 * @param {'user'|'candidate'} type - Specifies if we are impersonating a user or candidate
	 * @param {boolean} [localize=false] - Wether the user should be able to localize or not
	 * @return {Observable<AppUser | boolean>} - Returns the app user (if impersonating a user) on success, a boolean for candidates
	 */
	public impersonateCandidate(login: string, localize: boolean = false): Observable<boolean> {
		let url = `${this.settings.api}/impersonate?candidateLoginId=${login}`;
		if (localize) {
			url += '&enableLocalization=true';
		}
		return this.auth.updateTokenWithPost$(url).pipe(take(1));
	}

	/**
	 * Triggers a forgotten password email
	 * @method
	 * @param {string} [loginId] - The username for the password reset
	 * @returns {Observable<boolean>} - Returns an observable of a boolean set to true
	 */
	public forgotPasswordByUsername(loginId: string): Observable<boolean> {
		return this.forgotPassword({ loginId }, 'sponsor');
	}

	/**
	 * Triggers a forgotten password email using email
	 * @method
	 * @param {string} emailAddr - The email for the password reset
	 * @returns {Observable<boolean>} - Returns an observable of a boolean set to true
	 */
	public forgotPasswordByEmail(emailAddr: string): Observable<boolean> {
		return this.forgotPassword({ emailAddr }, 'sponsor');
	}

	/**
	 * Resets a password for a user
	 * @method
	 * @param {string} [token] - The token from the URL
	 * @param {string} [newPassword] - The new password
	 * @returns {Observable<boolean>} - Returns an observable of a boolean set to true
	 */
	public resetSponsorPassword(token: string, newPassword: string): Observable<boolean> {
		return this.resetPassword({ token, newPassword }, 'sponsor');
	}

	/**
	 * Loads the web user via GraphQL
	 * @private
	 * @method
	 * @param {string[]} features - A string array of features to append to the AppUser
	 * @param {CmPublicTokenData} token - The public data on the access token
	 * @return {Observable<AppUser>} - Returns an observable containing an AppUser derived from the web user
	 */
	private getUserQuery$(): (features: string[], token: CmPublicTokenData) => Observable<AppUser | null> {
		return (features: string[], token: CmPublicTokenData) =>
			!features
				? of(null)
				: this.apollo
						.watchQuery<{ webUserAccount: Partial<WebUserAccount> }>({
							query: QUERY_CURRENT_USER(features),
							fetchPolicy: 'network-only',
						})
						.valueChanges.pipe(
							switchMap(({ data }) => {
								return data.webUserAccount
									? of(data.webUserAccount)
									: throwError('Unable to load user');
							}),
							map(webUserAccount =>
								webUserAccount && webUserAccount.webUser
									? ({
											id: webUserAccount.webUser.id,
											name: webUserAccount.webUser.name,
											username: webUserAccount.login,
											email: webUserAccount.webUser.email,
											permissions: this.utilities.reduceNameKey(
												<{ name: string; key: string }[]>webUserAccount.permissionList || []
											),
											admin: webUserAccount.webUser.admin,
											internal: webUserAccount.webUser.internal,
											type: AppUserType.WEB_USER,
											candRestrictType: webUserAccount.webUser.candRestrictType,
											qlik:
												!!webUserAccount.webUser.qlik &&
												webUserAccount.webUser.qlik !== QlikCmRole.NoRole,
											isImpersonating: token.isImpersonating,
											localizationEnabled: token.localizationEnabled,
											features,
									  } as AppUser)
									: null
							)
						);
	}

	private getCanLocalizeHubStatus$(): Observable<boolean> {
		return this.user$.pipe(
			filter(user => !!user),
			map(user => {
				return LocalizationHubUsers.includes(((user || {}).username || '').toLowerCase());
			})
		);
	}
}
